import * as RadixTooltip from '@radix-ui/react-tooltip';
import type { HTMLAttributes, ReactElement, ReactNode, Ref } from 'react';
import styles from './Tooltip.module.css';

const OPEN_DELAY_MS = 450;
const SKIP_DELAY_MS = 250;

export const TooltipProvider = ({ children }: { readonly children: ReactNode }) => (
  <RadixTooltip.Provider delayDuration={OPEN_DELAY_MS} skipDelayDuration={SKIP_DELAY_MS}>
    {children}
  </RadixTooltip.Provider>
);

/**
 * Anything else given to the tooltip (by a parent that uses it as its own trigger, e.g. a menu)
 * is passed on to the child element, so a tooltip never breaks the trigger it wraps.
 */
export interface TooltipProps extends Omit<HTMLAttributes<HTMLElement>, 'content' | 'children'> {
  readonly content: ReactNode;
  readonly side?: 'top' | 'bottom' | 'left' | 'right' | undefined;
  /** A single focusable element that receives the hover/focus handlers. */
  readonly children: ReactElement;
}

export const Tooltip = ({
  content,
  side = 'top',
  children,
  ref,
  ...forwarded
}: TooltipProps & { readonly ref?: Ref<HTMLButtonElement> }) => (
  <RadixTooltip.Root>
    <RadixTooltip.Trigger asChild ref={ref} {...forwarded}>
      {children}
    </RadixTooltip.Trigger>
    <RadixTooltip.Portal>
      <RadixTooltip.Content side={side} sideOffset={6} collisionPadding={8} className={styles.tooltip}>
        {content}
      </RadixTooltip.Content>
    </RadixTooltip.Portal>
  </RadixTooltip.Root>
);
